import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { User, LogOut, Moon, Sun, Wrench } from 'lucide-react';
import { buildDashboardUrl } from '../lib/subdomain';
import { loadSiteContentPage } from '../lib/siteContent';
import { optimizedLogoUrl } from '../lib/assetUrl';

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [theme, setTheme] = useState('system');
  const [content, setContent] = useState({
    logo_url: '/logo.png',
    nav_home: 'Home',
    nav_library: 'Library',
    nav_tools: 'PDF Tools',
    nav_premium: 'Premium',
    nav_products: 'Digital Products',
    nav_about: 'About',
    nav_contact: 'Contact',
    login: 'Login',
    login_signup: 'Login / Sign Up',
    my_account: 'My Account'
  });
  const navigate = useNavigate();
  const location = useLocation();

  const applyTheme = (nextTheme) => {
    const root = document.documentElement;
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const useDark = nextTheme === 'dark' || (nextTheme === 'system' && prefersDark);
    root.classList.toggle('dark', useDark);
  };

  const isHome = location.pathname === '/';

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const goToSection = (sectionId) => {
    if (isHome) {
      scrollToSection(sectionId);
      return;
    }
    navigate('/', { state: { scrollTo: sectionId } });
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    try {
      const storedTheme = localStorage.getItem('theme') || 'system';
      setTheme(storedTheme);
      applyTheme(storedTheme);
    } catch {
      // ignore
    }

    // Check if user/admin is logged in
    const userToken = localStorage.getItem('userToken');
    const adminToken = localStorage.getItem('token');
    const adminUserRaw = localStorage.getItem('user');
    let admin = false;
    if (adminUserRaw) {
      try {
        const u = JSON.parse(adminUserRaw);
        admin = u?.role === 'admin';
      } catch {
        admin = false;
      }
    }
    setIsAdmin(admin);
    setIsLoggedIn(!!userToken || !!adminToken);

    fetchContent();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    try {
      localStorage.setItem('theme', nextTheme);
    } catch {
      // ignore
    }
    applyTheme(nextTheme);
  };

  const fetchContent = async () => {
    try {
      const items = await loadSiteContentPage('site_navbar');
      const contentMap = {};

      items.forEach((item) => {
        contentMap[item.key] = item.value;
      });

      setContent((prev) => ({ ...prev, ...contentMap }));
    } catch (error) {
      console.error('Error fetching navbar content:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setIsAdmin(false);
    setMobileMenuOpen(false);
    window.location.assign('/');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background shadow-md' : 'bg-background/95 backdrop-blur-sm'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <div className="flex items-center cursor-pointer" data-testid="navbar-logo">
            <button type="button" onClick={() => goToSection('hero')} className="cursor-pointer">
              <img 
                src={optimizedLogoUrl(content.logo_url)}
                alt="TinyPDFs Logo" 
                className="h-24"
                width="180"
                height="96"
                decoding="async"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              type="button"
              onClick={() => goToSection('hero')}
              className="text-sm font-medium cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
              data-testid="nav-link-hero"
            >
              {content.nav_home}
            </button>
            <RouterLink
              to="/pdfs"
              className="text-sm font-medium cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
              data-testid="nav-link-pdfs"
            >
              {content.nav_library}
            </RouterLink>
            <a
              href={buildDashboardUrl()}
              className="inline-flex items-center gap-1.5 text-sm font-medium cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
              data-testid="nav-link-tools"
            >
              <Wrench className="w-4 h-4" />
              {content.nav_tools}
            </a>
            <button
              type="button"
              onClick={() => goToSection('premium')}
              className="text-sm font-medium cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
              data-testid="nav-link-premium"
            >
              {content.nav_premium}
            </button>
            <RouterLink
              to="/products"
              className="text-sm font-medium cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
              data-testid="nav-link-products"
            >
              {content.nav_products}
            </RouterLink>
            <RouterLink
              to="/about"
              className="text-sm font-medium cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
              data-testid="nav-link-about"
            >
              {content.nav_about}
            </RouterLink>
            <button
              type="button"
              onClick={() => goToSection('contact')}
              className="text-sm font-medium cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
              data-testid="nav-link-contact"
            >
              {content.nav_contact}
            </button>

            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border bg-background hover:bg-accent transition-colors"
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4 text-foreground" />
              ) : (
                <Moon className="w-4 h-4 text-foreground" />
              )}
            </button>

            {/* Account Button */}
            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                <RouterLink
                  to={isAdmin ? '/admin/dashboard' : '/dashboard'}
                  className="flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                  style={{ backgroundColor: '#FFF5F3', color: '#F24A2E' }}
                  data-testid="nav-dashboard"
                >
                  <User className="w-4 h-4" />
                  {content.my_account}
                </RouterLink>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-lg transition-colors text-muted-foreground hover:bg-accent"
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            ) : (
              <RouterLink
                to="/login"
                className="flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                style={{ backgroundColor: '#F24A2E', color: 'white' }}
                data-testid="nav-login"
              >
                <User className="w-4 h-4" />
                {content.login}
              </RouterLink>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className="block w-full h-0.5 bg-foreground/70 transition-transform" />
              <span className="block w-full h-0.5 bg-foreground/70 transition-transform" />
              <span className="block w-full h-0.5 bg-foreground/70 transition-transform" />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-t" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            <button
              type="button"
              onClick={() => {
                goToSection('hero');
                closeMobileMenu();
              }}
              className="block text-left w-full text-sm font-medium py-2 cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
            >
              {content.nav_home}
            </button>
            <RouterLink
              to="/pdfs"
              onClick={closeMobileMenu}
              className="block text-sm font-medium py-2 cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
            >
              {content.nav_library}
            </RouterLink>
            <a
              href={buildDashboardUrl()}
              onClick={closeMobileMenu}
              className="flex items-center gap-2 text-sm font-medium py-2 cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
            >
              <Wrench className="w-4 h-4" />
              {content.nav_tools}
            </a>
            <button
              type="button"
              onClick={() => {
                goToSection('premium');
                closeMobileMenu();
              }}
              className="block text-left w-full text-sm font-medium py-2 cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
            >
              {content.nav_premium}
            </button>
            <RouterLink
              to="/products"
              onClick={closeMobileMenu}
              className="block text-sm font-medium py-2 cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
            >
              {content.nav_products}
            </RouterLink>
            <RouterLink
              to="/about"
              onClick={closeMobileMenu}
              className="block text-sm font-medium py-2 cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
            >
              {content.nav_about}
            </RouterLink>
            <button
              type="button"
              onClick={() => {
                goToSection('contact');
                closeMobileMenu();
              }}
              className="block text-left w-full text-sm font-medium py-2 cursor-pointer transition-colors text-muted-foreground hover:text-[#F24A2E]"
            >
              {content.nav_contact}
            </button>

            <button
              type="button"
              onClick={() => {
                toggleTheme();
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-2 text-sm font-medium py-2"
              data-testid="mobile-theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4" style={{ color: '#F24A2E' }} />
              ) : (
                <Moon className="w-4 h-4" style={{ color: '#F24A2E' }} />
              )}
              <span className="text-muted-foreground">Toggle theme</span>
            </button>
            
            {/* Mobile Account Link */}
            <div className="pt-2 border-t">
              {isLoggedIn ? (
                <>
                  <RouterLink
                    to={isAdmin ? '/admin/dashboard' : '/dashboard'}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-sm font-medium py-2"
                    style={{ color: '#F24A2E' }}
                  >
                    <User className="w-4 h-4" />
                    {content.my_account}
                  </RouterLink>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-sm font-medium py-2"
                    style={{ color: '#555555' }}
                    data-testid="mobile-nav-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <RouterLink
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-sm font-medium py-2"
                  style={{ color: '#F24A2E' }}
                >
                  <User className="w-4 h-4" />
                  {content.login_signup}
                </RouterLink>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
